import { CrewChange } from '../types';

export interface CrewStint {
  /** Whether the employee is currently aboard (latest change is sign_on/continue). */
  aboard: boolean;
  /**
   * The changeDate of the sign_on that began the current uninterrupted stay.
   * A `continue` keeps the existing stint — it does not count as a new join.
   * Null when the employee is not aboard.
   */
  joinDate: string | null;
  /** The changeDate of the employee's most recent crew change. */
  lastUpdate: string | null;
  /** The employee's most recent crew change record (for branch/vessel). */
  latest: CrewChange | null;
}

/**
 * Walks an employee's crew-change history chronologically. A sign_on starts a
 * new stint, a continue extends it, a sign_off ends it.
 */
export function resolveCrewStint(changes: CrewChange[]): CrewStint {
  const sorted = [...changes].sort((a, b) => {
    const byDate = (a.changeDate || '').localeCompare(b.changeDate || '');
    if (byDate !== 0) return byDate;
    return (a.createdAt || '').localeCompare(b.createdAt || '');
  });

  let stintStart: string | null = null;
  for (const change of sorted) {
    if (change.status === 'sign_on') stintStart = change.changeDate;
    else if (change.status === 'sign_off') stintStart = null;
  }

  const latest = sorted[sorted.length - 1] || null;
  const aboard = !!latest && (latest.status === 'sign_on' || latest.status === 'continue');
  return {
    aboard,
    joinDate: aboard ? stintStart ?? latest!.changeDate : null,
    lastUpdate: latest?.changeDate ?? null,
    latest,
  };
}
